import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaBuilding, FaUserTie, FaCalendarAlt, FaDollarSign, FaUsers } from 'react-icons/fa';
import '../pages_premium.css';

function HiremateJobDetailView() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchJob = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/admin/hiremate-jobs/${id}`);
            if (res.data) {
                setJob(res.data);
            }
        } catch (error) {
            console.error('Error fetching job details:', error);
            toast.error('Failed to load job posting details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJob();
    }, [id]);

    const formatDateTimeSingleLine = (jobData) => {
        if (!jobData) return 'Flexible';
        let dateStr = '';
        if (jobData.date_type === 'flexible') {
            dateStr = 'Flexible';
        } else if (jobData.task_date) {
            dateStr = jobData.task_date;
        }

        let timeStr = jobData.time_of_day || '';
        if (jobData.shifts) {
            let shiftsArr = [];
            try {
                shiftsArr = typeof jobData.shifts === 'string' ? JSON.parse(jobData.shifts) : jobData.shifts;
            } catch (e) {
                shiftsArr = [];
            }
            if (Array.isArray(shiftsArr) && shiftsArr.length > 0) {
                const first = shiftsArr[0];
                if (first.date) dateStr = first.date;
                if (first.start_time && first.end_time) {
                    timeStr = `${first.start_time} - ${first.end_time}`;
                } else if (first.start_time) {
                    timeStr = first.start_time;
                }
            }
        }

        if (dateStr && timeStr) return `${dateStr} • ${timeStr}`;
        return dateStr || timeStr || 'Flexible';
    };

    if (loading) {
        return (
            <div className="page-premium-wrapper">
                <div className="page-premium-content text-center py-5 text-secondary">
                    Loading job posting details...
                </div>
            </div>
        );
    }

    if (!job) {
        return (
            <div className="page-premium-wrapper">
                <div className="page-premium-content text-center py-5">
                    <p style={{ color: '#ef4444', fontWeight: '700' }}>Job Posting Not Found</p>
                    <button
                        onClick={() => navigate('/hiremate-jobs')}
                        style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: '#334155', color: '#fff', border: 'none', cursor: 'pointer' }}
                    >
                        Back to Posted Jobs List
                    </button>
                </div>
            </div>
        );
    }

    const business = job.Business || {};
    const owner = business.BusinessOwner || job.Poster || {};
    const isHourly = job.budget_type === 'hourly';

    return (
        <div className="page-premium-wrapper hiremate-job-view-page">
            <div className="page-premium-content">

                {/* Back Button & Actions */}
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <button
                        onClick={() => navigate('/hiremate-jobs')}
                        style={{
                            backgroundColor: '#ffffff',
                            color: '#334155',
                            border: '1px solid #cbd5e1',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                    >
                        <FaArrowLeft /> Back to Posted Jobs List
                    </button>

                    <button
                        onClick={() => navigate(`/hiremate-jobs/${job.id}/requests`)}
                        style={{
                            backgroundColor: '#4f46e5',
                            color: '#ffffff',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '700',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 2px 4px rgba(79, 70, 229, 0.2)'
                        }}
                    >
                        <FaUsers /> View All Candidate Requests
                    </button>
                </div>

                {/* Hero Job Banner */}
                <div style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '20px',
                    padding: '24px 28px',
                    marginBottom: '24px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '20px'
                }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <span style={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontSize: '12px', fontWeight: '700', padding: '3px 10px', borderRadius: '10px' }}>
                                {job.category || 'Staff Hiring'}
                            </span>
                            {job.role && (
                                <span style={{ backgroundColor: '#f3e8ff', color: '#6b21a8', fontSize: '12px', fontWeight: '700', padding: '3px 10px', borderRadius: '10px' }}>
                                    {job.role}
                                </span>
                            )}
                        </div>
                        <h2 style={{ margin: 0, fontWeight: '800', color: '#0f172a', fontSize: '1.5rem' }}>
                            {job.title}
                        </h2>
                        <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaBuilding style={{ color: '#0ab68e' }} /> {job.company_name || business.company_name || 'Individual'} • Posted by {owner.name || 'Owner'}
                        </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                        <span style={{
                            backgroundColor: '#f1f5f9',
                            color: '#0f172a',
                            padding: '10px 18px',
                            borderRadius: '16px',
                            fontWeight: '800',
                            fontSize: '16px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <FaDollarSign style={{ color: '#0ab68e' }} /> ${parseFloat(job.budget || 0).toFixed(2)} {isHourly ? '/ hr' : '(Total Fixed)'}
                        </span>
                    </div>
                </div>

                {/* Main Details Grid */}
                <div className="admin-2col-grid" style={{ marginBottom: '24px' }}>

                    {/* Job Description */}
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaBuilding style={{ color: '#0ab68e' }} /> Job Description & Requirements
                        </h5>
                        <p style={{ color: '#475569', fontSize: '14px', lineHeight: '1.6', margin: 0, whiteSpace: 'pre-line' }}>
                            {job.description || 'No description provided.'}
                        </p>
                    </div>

                    {/* Business Owner & Employer Info */}
                    <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                        <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaUserTie style={{ color: '#4f46e5' }} /> Business Owner & Employer Details
                        </h5>
                        <div style={{ fontSize: '14px', color: '#334155', spaceY: '6px' }}>
                            <p style={{ margin: '4px 0' }}><strong>Company Name:</strong> {job.company_name || business.company_name || 'Individual Employer'}</p>
                            <p style={{ margin: '4px 0' }}><strong>Contact Person:</strong> {job.contact_person || owner.name || '-'}</p>
                            <p style={{ margin: '4px 0' }}><strong>Email:</strong> {owner.email || '-'}</p>
                            <p style={{ margin: '4px 0' }}><strong>Phone:</strong> {job.contact_value || owner.phone || '-'}</p>
                            {business.abn && <p style={{ margin: '4px 0' }}><strong>ABN:</strong> {business.abn}</p>}
                        </div>
                    </div>

                </div>

                {/* Schedule & Location */}
                <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginBottom: '24px' }}>
                    <h5 style={{ fontWeight: '700', color: '#0f172a', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaCalendarAlt style={{ color: '#0ab68e' }} /> Schedule & Work Location
                    </h5>
                    <div className="admin-2col-grid">
                        <div>
                            <p style={{ margin: '4px 0', fontSize: '14px', color: '#334155' }}><strong>Shift Date & Timing:</strong> {formatDateTimeSingleLine(job)}</p>
                            <p style={{ margin: '4px 0', fontSize: '14px', color: '#334155' }}><strong>Date Preference:</strong> {job.date_type === 'on_date' ? 'Specific On-Date Shift' : 'Flexible Dates'}</p>
                        </div>
                        <div>
                            <p style={{ margin: '4px 0', fontSize: '14px', color: '#334155' }}><strong>Location Type:</strong> {job.location_type === 'online' ? 'Online / Remote' : 'In Person'}</p>
                            {job.suburb && <p style={{ margin: '4px 0', fontSize: '14px', color: '#334155' }}><strong>Suburb / Address:</strong> {job.suburb}</p>}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default HiremateJobDetailView;
